import React from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { ThemeProvider, CssBaseline, GlobalStyles } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Helmet } from "react-helmet-async";

import theme from "./theme/theme";
import {
  Login,
  Register,
  Unauthorized,
  VcatUserHome,
  VcatUserInfo,
  ClientAdminHome,
  ClientAdminInfo,
  ClientAdminBilling,
  ClientAdminScenarios,
  ClientUserDashboard,
  ClientAdminSessions,
  EyeviationSuperUserHome,
  EyeviationSuperUserInfo,
} from "./pages";
import ProtectedRoute from "./components/ProtectedRoute";
import { AccountType, Role, UserType } from "./types/enums";
import ResetPassword from "./pages/Shared/ResetPassword";
import AccountRootsPage from "./pages/EyeviationSuperUser/AccountRootsPage";
import ClientAdminsPage from "./pages/EyeviationSuperUser/ClientAdminsPage";
import VCatKitsPage from "./pages/EyeviationSuperUser/VcatKitsPage";
import AllActiveSessionsPage from "./pages/EyeviationSuperUser/ActiveSessionsPage";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import SessionDetail from "./components/ShootingData/SessionDetail";
import RepetitionDetail from "./components/ShootingData/RepetitionDetail";
import ShootingDataPage from "./pages/Shared/ShootingDataPage";
import DrillDetail from "./components/ShootingData/DrillDetail";


const queryClient = new QueryClient();

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Helmet>
          <title>VCAT</title>
        </Helmet>
        <GlobalStyles
          styles={{
            ".auth-page input:-webkit-autofill": {
              WebkitBoxShadow: "0 0 0 1000px #0E1720 inset",
              WebkitTextFillColor: "#FFFFFF",
              transition: "background-color 9999s ease-in-out 0s",
            },
            ".auth-page input:-webkit-autofill:focus": {
              WebkitBoxShadow: "0 0 0 1000px #0E1720 inset",
            },
          }}
        />

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route path="reset-password" element={<ResetPassword />} />

            <Route
              path="/vcat-user"
              element={
                <ProtectedRoute
                  allowedTypes={[UserType.PERSONAL]}
                  allowedRoles={[null]}
                  allowedAccounts={[null]}
                >
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route element={<VcatUserHome />}>
                <Route index element={<Navigate to="user-info" replace />} />

                {/* /vcat-user/user-info */}
                <Route path="user-info" element={<VcatUserInfo />} />

                {/* /vcat-user/history */}
                {/* <Route path="history" element={<VcatUserHistory />} /> */}

                    <Route
                  path="shooting-data"
                  element={<ShootingDataPage />}
                />

                {/* show the session detail page (replaces the list) */}
                <Route
                  path="shooting-data/:sessionId"
                  element={<SessionDetail />}
                />

                {/* show the repetitions page */}
                <Route
                  path="shooting-data/:sessionId/scenario/:scenarioId/repetitions"
                  element={<RepetitionDetail />}
                />
                <Route
                  path="shooting-data/:sessionId/scenario/:scenarioId/repetitions/:drillName/:repetitionNumber"
                  element={<DrillDetail />}
                />
              </Route>
            </Route>

            <Route
              path="/client-admin"
              element={
                <ProtectedRoute
                  allowedTypes={[UserType.MANAGED]}
                  allowedAccounts={[AccountType.CLIENT, AccountType.EYEVIATION]}
                  allowedRoles={[Role.ADMIN, Role.SUPER_USER]}
                >
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route element={<ClientAdminHome />}>
                <Route index element={<Navigate to="/client-admin/billing" />} />
                <Route path="billing" element={<ClientAdminBilling />} />
                <Route path="scenarios" element={<ClientAdminScenarios />} />
                <Route path="sessions" element={<ClientAdminSessions />} />
                <Route path="user-info" element={<ClientAdminInfo />} />
                {/* show the list */}
                <Route
                  path="shooting-data"
                  element={<ShootingDataPage />}
                />

                {/* show the session detail page (replaces the list) */}
                <Route
                  path="shooting-data/:sessionId"
                  element={<SessionDetail />}
                />

                {/* show the repetitions page */}
                <Route
                  path="shooting-data/:sessionId/scenario/:scenarioId/repetitions"
                  element={<RepetitionDetail />}
                />
                <Route
                  path="shooting-data/:sessionId/scenario/:scenarioId/repetitions/:drillName/:repetitionNumber"
                  element={<DrillDetail />}
                />
              </Route>
            </Route>

            <Route
              path="/client-user"
              element={
                <ProtectedRoute
                  allowedRoles={[Role.USER]}
                  allowedTypes={[UserType.PERSONAL]}
                  allowedAccounts={[]}
                >
                  <ClientUserDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/eyeviation-super"
              element={
                <ProtectedRoute
                  allowedTypes={[UserType.MANAGED]}
                  allowedAccounts={[AccountType.CLIENT, AccountType.EYEVIATION]}
                  allowedRoles={[Role.SUPER_USER]}
                >
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route element={<EyeviationSuperUserHome />}>
                <Route
                  index
                  element={<Navigate to="/eyeviation-super/account-roots" />}
                />
                <Route path="user-info" element={<EyeviationSuperUserInfo />} />
                <Route path="account-roots" element={<AccountRootsPage />} />
                <Route path="client-admins" element={<ClientAdminsPage />} />
                <Route path="vcat-kits" element={<VCatKitsPage />} />
                <Route
                  path="active-sessions"
                  element={<AllActiveSessionsPage />}
                />
              </Route>
            </Route>
          </Routes>
        </LocalizationProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
